import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { formatDuration, formatCost, formatCallDate, getCallStatusColor, getCallSuccessText, getCallPerformanceScore, getPerformanceScoreColor } from '../../../../../services/callService';
import { useAuth } from '../../../../../contexts/AuthContext';
import RecordingPlayer from '../shared/RecordingPlayer';

const CallDetailsModal = ({ call, type, onClose }) => {
  const { userRole } = useAuth();
  const [activeView, setActiveView] = useState(type || 'details');

  if (!call) return null;

  const performanceScore = getCallPerformanceScore(call);

  // Define which tabs are available based on user role and available data
  const getAvailableTabs = () => {
    const tabs = [];
    
    // Details tab - only for Super Admin
    if (userRole === 'SUPER_ADMIN') {
      tabs.push({ id: 'details', label: 'Details', icon: '📋' });
    }

    const reportTabs = [];
    const hasNewReports = call.sa_report || call.coach_report || call.client_report;

    // If we have new report data, show the appropriate report tabs
    if (hasNewReports) {
      // Super Admin can see all report tabs
      if (userRole === 'SUPER_ADMIN') {
        reportTabs.push(
          { id: 'sa_report', label: 'SA Report', icon: '👑', disabled: !call.sa_report },
          { id: 'coach_report', label: 'Coach Report', icon: '🎯', disabled: !call.coach_report },
          { id: 'client_report', label: 'Client Report', icon: '👤', disabled: !call.client_report }
        );
      }
      // Coach can see Coach Report and Client Report
      else if (userRole === 'COACH') {
        reportTabs.push(
          { id: 'coach_report', label: 'Coach Report', icon: '🎯', disabled: !call.coach_report },
          { id: 'client_report', label: 'Client Report', icon: '👤', disabled: !call.client_report }
        );
      }
    } else if (call.report_summary) {
      // Fallback to Summary tab for older records
      reportTabs.push(
        { id: 'summary', label: 'Summary', icon: '📝', disabled: !call.report_summary }
      );
    }
    
    // Add report tabs
    tabs.push(...reportTabs);
    
    // Transcript tab - available for all users
    tabs.push({ id: 'transcript', label: 'Transcript', icon: '📜', disabled: !call.report_transcript });
    
    return tabs;
  };

  const modalTabs = getAvailableTabs();

  const renderDetails = () => (
    <div className="space-y-6">
      {/* Call Header */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-gray-900">Contact Information</h4>
          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getCallStatusColor(call.call_status, call.success_evaluation)}`}>
            {getCallSuccessText(call.success_evaluation, call.ended_reason)}
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Contact ID:</span>
            <span className="ml-2 text-gray-900 font-mono text-xs">{call.call_id ?? call.provider_call_id}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Assistant:</span>
            <span className="ml-2 text-gray-900">{call.assistant_name || 'Unknown'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Started:</span>
            <span className="ml-2 text-gray-900">{formatCallDate(call.call_started_at)}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Ended:</span>
            <span className="ml-2 text-gray-900">{formatCallDate(call.call_ended_at)}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Duration:</span>
            <span className="ml-2 text-gray-900">{formatDuration(call.duration_minutes)}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Status:</span>
            <span className="ml-2 text-gray-900">{call.call_status || 'Unknown'}</span>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Performance Metrics</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-3 border">
            <div className="text-center">
              <div className={`text-2xl font-bold ${getPerformanceScoreColor(performanceScore)}`}>
                {performanceScore}%
              </div>
              <div className="text-xs text-gray-600">Performance Score</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-3 border">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">
                {call.model_latency_average ? `${Math.round(call.model_latency_average)}ms` : 'N/A'}
              </div>
              <div className="text-xs text-gray-600">Model Latency</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-3 border">
            <div className="text-center">
              <div className="text-lg font-bold text-gray-900">
                {call.voice_latency_average ? `${Math.round(call.voice_latency_average)}ms` : 'N/A'}
              </div>
              <div className="text-xs text-gray-600">Voice Latency</div>
            </div>
          </div>
        </div>
      </div>

      {/* Cost Information */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Cost Breakdown</h4>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-white rounded-lg p-3 border text-center">
            <div className="text-lg font-bold text-gray-900">{formatCost(call.total_cost)}</div>
            <div className="text-xs text-gray-600">Total Cost</div>
          </div>
          <div className="bg-white rounded-lg p-3 border text-center">
            <div className="text-lg font-bold text-gray-900">{formatCost(call.llm_cost)}</div>
            <div className="text-xs text-gray-600">LLM Cost</div>
          </div>
          <div className="bg-white rounded-lg p-3 border text-center">
            <div className="text-lg font-bold text-gray-900">{formatCost(call.stt_cost)}</div>
            <div className="text-xs text-gray-600">STT Cost</div>
          </div>
          <div className="bg-white rounded-lg p-3 border text-center">
            <div className="text-lg font-bold text-gray-900">
              {formatCost(call.provider === 'retell' ? call.retell_cost : call.vapi_cost)}
            </div>
            <div className="text-xs text-gray-600">{call.provider === 'retell' ? 'Retell Cost' : 'VAPI Cost'}</div>
          </div>
        </div>
      </div>

      {/* Technical Details */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Technical Details</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Voice Provider:</span>
            <span className="ml-2 text-gray-900">{call.voice_provider || 'N/A'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Transcriber Provider:</span>
            <span className="ml-2 text-gray-900">{call.transcriber_provider || 'N/A'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">LLM Tokens (Prompt):</span>
            <span className="ml-2 text-gray-900">{call.llm_prompt_tokens || 'N/A'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">LLM Tokens (Completion):</span>
            <span className="ml-2 text-gray-900">{call.llm_completion_tokens || 'N/A'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">TTS Characters:</span>
            <span className="ml-2 text-gray-900">{call.tts_characters || 'N/A'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">End Reason:</span>
            <span className="ml-2 text-gray-900">{call.ended_reason || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Links and Actions */}
      {call.log_url && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Logs</h4>
          
          <div className="flex flex-wrap gap-3">
            <a
              href={call.log_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              📊 View Logs
            </a>
          </div>
        </div>
      )}
    </div>
  );

  const renderSAReport = () => (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-3">Super Admin Report</h4>
        {call.sa_report ? (
          <div className="bg-white rounded-lg p-4 border">
            <div
              className="text-gray-700 leading-relaxed"
              style={{
                '& ul': {
                  listStyleType: 'disc',
                  paddingLeft: '1.5rem',
                  marginLeft: '1rem'
                },
                '& ol': {
                  listStyleType: 'decimal',
                  paddingLeft: '1.5rem',
                  marginLeft: '1rem'
                },
                '& li': {
                  marginBottom: '0.25rem'
                }
              }}
            >
              <style dangerouslySetInnerHTML={{
                __html: `
                  .report-content ul {
                    list-style-type: disc;
                    padding-left: 1.5rem;
                    margin-left: 1rem;
                  }
                  .report-content ol {
                    list-style-type: decimal;
                    padding-left: 1.5rem;
                    margin-left: 1rem;
                  }
                  .report-content li {
                    margin-bottom: 0.25rem;
                  }
                `
              }} />
              <div
                className="report-content"
                dangerouslySetInnerHTML={{ __html: call.sa_report }}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">👑</div>
            <p>No Super Admin report available for this contact</p>
          </div>
        )}
      </div>
      
      {/* Quick stats for SA report view */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-gray-900">{formatDuration(call.duration_minutes)}</div>
          <div className="text-xs text-gray-600">Duration</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className={`text-lg font-bold ${getPerformanceScoreColor(performanceScore)}`}>
            {performanceScore}%
          </div>
          <div className="text-xs text-gray-600">Performance</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-gray-900">{formatCost(call.total_cost)}</div>
          <div className="text-xs text-gray-600">Cost</div>
        </div>
      </div>
    </div>
  );

  const renderCoachReport = () => (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-3">Coach Report</h4>
        {call.coach_report ? (
          <div className="bg-white rounded-lg p-4 border">
            <div className="text-gray-700 leading-relaxed">
              <style dangerouslySetInnerHTML={{
                __html: `
                  .report-content ul {
                    list-style-type: disc;
                    padding-left: 1.5rem;
                    margin-left: 1rem;
                  }
                  .report-content ol {
                    list-style-type: decimal;
                    padding-left: 1.5rem;
                    margin-left: 1rem;
                  }
                  .report-content li {
                    margin-bottom: 0.25rem;
                  }
                `
              }} />
              <div
                className="report-content"
                dangerouslySetInnerHTML={{ __html: call.coach_report }}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">🎯</div>
            <p>No Coach report available for this contact</p>
          </div>
        )}
      </div>
      
      {/* Quick stats for coach report view */}
       <div className={`grid ${userRole === 'SUPER_ADMIN' ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-gray-900">{formatDuration(call.duration_minutes)}</div>
          <div className="text-xs text-gray-600">Duration</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className={`text-lg font-bold ${getPerformanceScoreColor(performanceScore)}`}>
            {performanceScore}%
          </div>
          <div className="text-xs text-gray-600">Performance</div>
        </div>
        {userRole === 'SUPER_ADMIN' && (
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-gray-900">{formatCost(call.total_cost)}</div>
            <div className="text-xs text-gray-600">Cost</div>
          </div>
        )}
      </div>
    </div>
  );

  const renderClientReport = () => (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-3">Client Report</h4>
        {call.client_report ? (
          <div className="bg-white rounded-lg p-4 border">
            <div className="text-gray-700 leading-relaxed">
              <style dangerouslySetInnerHTML={{
                __html: `
                  .report-content ul {
                    list-style-type: disc;
                    padding-left: 1.5rem;
                    margin-left: 1rem;
                  }
                  .report-content ol {
                    list-style-type: decimal;
                    padding-left: 1.5rem;
                    margin-left: 1rem;
                  }
                  .report-content li {
                    margin-bottom: 0.25rem;
                  }
                `
              }} />
              <div
                className="report-content"
                dangerouslySetInnerHTML={{ __html: call.client_report }}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">👤</div>
            <p>No Client report available for this contact</p>
          </div>
        )}
      </div>
      
      {/* Quick stats for client report view - hidden for CLIENT role */}
      {userRole !== 'CLIENT' && (
        <div className={`grid ${userRole === 'SUPER_ADMIN' ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-lg font-bold text-gray-900">{formatDuration(call.duration_minutes)}</div>
            <div className="text-xs text-gray-600">Duration</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className={`text-lg font-bold ${getPerformanceScoreColor(performanceScore)}`}>
              {performanceScore}%
            </div>
            <div className="text-xs text-gray-600">Performance</div>
          </div>
          {userRole === 'SUPER_ADMIN' && (
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{formatCost(call.total_cost)}</div>
              <div className="text-xs text-gray-600">Cost</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderSummary = () => {
    try {
      return (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3">Contact Summary</h4>
            {call.report_summary ? (
              <div className="bg-white rounded-lg p-4 border">
                <div className="text-gray-700 leading-relaxed prose prose-sm max-w-none">
                  <ReactMarkdown
                    components={{
                      ul: ({ children, ...props }) => (
                        <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', marginBottom: '1rem' }} {...props}>{children}</ul>
                      ),
                      ol: ({ children, ...props }) => (
                        <ol style={{ listStyleType: 'decimal', paddingLeft: '1.5rem', marginBottom: '1rem' }} {...props}>{children}</ol>
                      ),
                      li: ({ children, ...props }) => (
                        <li style={{ marginBottom: '0.25rem' }} {...props}>{children}</li>
                      ),
                      h1: ({ children, ...props }) => (
                        <h1 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '1rem' }} {...props}>{children}</h1>
                      ),
                      h2: ({ children, ...props }) => (
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '1rem' }} {...props}>{children}</h2>
                      ),
                      h3: ({ children, ...props }) => (
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '1rem' }} {...props}>{children}</h3>
                      ),
                      p: ({ children, ...props }) => (
                        <p style={{ marginBottom: '1rem' }} {...props}>{children}</p>
                      ),
                      strong: ({ children, ...props }) => (
                        <strong style={{ fontWeight: '600' }} {...props}>{children}</strong>
                      ),
                      em: ({ children, ...props }) => (
                        <em style={{ fontStyle: 'italic' }} {...props}>{children}</em>
                      )
                    }}
                  >
                    {call.report_summary}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📝</div>
                <p>No summary available for this contact</p>
              </div>
            )}
          </div>
          
          {/* Quick stats for summary view */}
          <div className={`grid ${userRole === 'SUPER_ADMIN' ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-gray-900">{formatDuration(call.duration_minutes)}</div>
              <div className="text-xs text-gray-600">Duration</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className={`text-lg font-bold ${getPerformanceScoreColor(performanceScore)}`}>
                {performanceScore}%
              </div>
              <div className="text-xs text-gray-600">Performance</div>
            </div>
            {userRole === 'SUPER_ADMIN' && (
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-gray-900">{formatCost(call.total_cost)}</div>
                <div className="text-xs text-gray-600">Cost</div>
              </div>
            )}
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error rendering summary:', error);
      return (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-3">Contact Summary</h4>
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {call.report_summary}
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderTranscript = () => (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-3">Contact Transcript</h4>
        {call.report_transcript ? (
          <div className="bg-white rounded-lg p-4 border max-h-96 overflow-y-auto">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
              {call.report_transcript}
            </pre>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📜</div>
            <p>No transcript available for this contact</p>
          </div>
        )}
      </div>
      
      {/* Transcript metadata - only for Super Admin */}
      {userRole === 'SUPER_ADMIN' && call.report_transcript && (
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-sm text-gray-600 text-center">
            Transcript generated using {call.transcriber_provider || 'unknown'} • 
            {call.transcriber_model && ` Model: ${call.transcriber_model} • `}
            {call.report_transcript.length} characters
          </div>
        </div>
      )}
    </div>
  );

  const renderActiveView = () => {
    switch (activeView) {
      case 'sa_report':
        return renderSAReport();
      case 'coach_report':
        return renderCoachReport();
      case 'client_report':
        return renderClientReport();
      case 'summary':
        return renderSummary();
      case 'transcript':
        return renderTranscript();
      default:
        return renderDetails();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start mb-4 pb-4 border-b">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Contact Details</h3>
            <p className="text-sm text-gray-600">
              {call.assistant_name} • {formatCallDate(call.call_started_at)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8">
            {modalTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveView(tab.id)}
                disabled={tab.disabled}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                  activeView === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : tab.disabled
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.disabled && <span className="text-xs">(N/A)</span>}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {renderActiveView()}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
          >
            Close
          </button>
          {/* Only Super Admins can access recordings */}
          {userRole === 'SUPER_ADMIN' && call.recording_url && (
            <RecordingPlayer callId={call.call_id ?? call.provider_call_id} variant="button" />
          )}
        </div>
      </div>
    </div>
  );
};

export default CallDetailsModal;